// components/ui/Pagination.tsx
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { clsx } from 'clsx';
import React from 'react';

const PAGE_SIZE_OPTIONS = [12, 24, 48, 100];

export function Pagination({
    currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange
}: {
    currentPage: number; totalPages: number; totalItems: number; pageSize: number;
    onPageChange: (page: number) => void; onPageSizeChange: (size: number) => void;
}) {
    if (totalItems === 0) return null;

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    const pageNumbers: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
    } else {
        pageNumbers.push(1);
        if (currentPage > 3) pageNumbers.push('ellipsis');
        const start = Math.max(2, currentPage - 1);
        const end = Math.min(totalPages - 1, currentPage + 1);
        for (let i = start; i <= end; i++) pageNumbers.push(i);
        if (currentPage < totalPages - 2) pageNumbers.push('ellipsis');
        pageNumbers.push(totalPages);
    }

    return (
        <div className="px-6 py-4 bg-slate-50/50 dark:bg-slate-900/60 border-t border-slate-100 dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                    Showing <span className="font-semibold text-slate-900 dark:text-slate-100">{startItem}</span> to{' '}
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{endItem}</span> of{' '}
                    <span className="font-semibold text-slate-900 dark:text-slate-100">{totalItems.toLocaleString()}</span>
                </p>
                <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}
                    className="px-2 py-1 border border-slate-200 dark:border-slate-700 rounded-lg text-xs bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-200">
                    {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size} per page</option>)}
                </select>
            </div>
            <div className="flex gap-2 flex-wrap">
                <button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-30" type="button"><ChevronsLeft className="w-4 h-4" /></button>
                <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition flex items-center gap-1 text-slate-700 dark:text-slate-200" type="button"><ChevronLeft className="w-4 h-4" /> Prev</button>
                {pageNumbers.map((page, idx) => (
                    <React.Fragment key={idx}>
                        {page === 'ellipsis' ? <span className="px-2 py-2 text-slate-400 dark:text-slate-500">...</span> : (
                            <button onClick={() => onPageChange(page as number)}
                                className={clsx('px-4 py-2 rounded-xl text-sm font-medium transition', currentPage === page ? 'bg-gradient-to-r from-teal-500 to-teal-600 text-white shadow-md shadow-teal-500/25' : 'border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200')} type="button">{page}</button>
                        )}
                    </React.Fragment>
                ))}
                <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} className="px-4 py-2 border border-slate-200 dark:border-slate-700 rounded-xl text-sm disabled:opacity-40 hover:bg-slate-100 dark:hover:bg-slate-800 font-medium transition flex items-center gap-1 text-slate-700 dark:text-slate-200" type="button">Next <ChevronRight className="w-4 h-4" /></button>
                <button onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} className="p-2 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 disabled:opacity-30" type="button"><ChevronsRight className="w-4 h-4" /></button>
            </div>
        </div>
    );
}